import {
  DEFAULT_SEARCH_WEIGHTS,
  MAX_SEARCH_V2_KEYWORDS,
  MS_PER_DAY,
} from "@/lib/constants";
import { jobs } from "@/lib/jobs";
import { ClassifiedKeyword, SearchV2Filters, SearchV2Weights } from "@/types";

const SEARCH_V2_FILTERS_STORAGE_KEY = "searchV2FiltersByJobId";
const SEARCH_V2_FILTERS_TTL_MS = 30 * MS_PER_DAY;

// Typed against ClassifiedKeyword so an invalid value can't be listed here.
// Keep in sync if the unions in @/types gain a member.
const KEYWORD_CATEGORIES: readonly NonNullable<
  ClassifiedKeyword["category"]
>[] = [
  "Skills",
  "Equipment",
  "Processes",
  "Industries",
  "Certifications",
  "Soft Skills",
  "Safety",
];
const KEYWORD_COLORS: readonly NonNullable<ClassifiedKeyword["color"]>[] = [
  "blue",
  "violet",
  "teal",
  "orange",
  "green",
  "slate",
  "red",
];

interface StoredSearchV2Filters {
  expiresAt: number;
  filters: SearchV2Filters;
  weights: SearchV2Weights | null;
}

export interface StoredSearchV2Settings {
  filters: SearchV2Filters;
  weights: SearchV2Weights | null;
}

/* Parsing */
// localStorage is the only place untyped data enters this module, so `unknown`
// is confined to these parsers. Anything that fails validation is dropped
// rather than trusted — callers get null and fall back to default filters.

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

// Array.isArray narrows `unknown` to `any[]`, which loses element checking.
const isUnknownArray = (value: unknown): value is unknown[] =>
  Array.isArray(value);

const isStringArray = (value: unknown): value is string[] =>
  isUnknownArray(value) && value.every((item) => typeof item === "string");

const isKeywordCategory = (
  value: unknown,
): value is ClassifiedKeyword["category"] => {
  if (value === null) return true;
  if (typeof value !== "string") return false;
  return KEYWORD_CATEGORIES.some((category) => category === value);
};

const isKeywordColor = (
  value: unknown,
): value is ClassifiedKeyword["color"] => {
  if (value === null) return true;
  if (typeof value !== "string") return false;
  return KEYWORD_COLORS.some((color) => color === value);
};

// Entries written before keywords became objects hold a comma-separated string.
const parseStoredKeywords = (value: unknown): ClassifiedKeyword[] | null => {
  if (typeof value === "string") {
    return jobs.parseKeywords(value).map((keyword) => ({
      category: null,
      color: null,
      keyword,
    }));
  }
  if (!isUnknownArray(value)) return null;

  const keywords: ClassifiedKeyword[] = [];
  for (const item of value) {
    if (!isRecord(item)) return null;

    const { category, color, keyword } = item;
    if (
      typeof keyword !== "string" ||
      !isKeywordCategory(category) ||
      !isKeywordColor(color)
    ) {
      return null;
    }

    keywords.push({ category, color, keyword });
  }

  // Writes are capped upstream; re-cap on restore so a tampered or legacy
  // entry can't push an oversized keyword list into the search request.
  return keywords.slice(0, MAX_SEARCH_V2_KEYWORDS);
};

const parseStoredWeights = (value: unknown): SearchV2Weights | null => {
  if (!isRecord(value)) return null;

  const { activityWeight, semanticWeight, textWeight, weRecencyWeight } =
    value;
  if (
    typeof activityWeight !== "number" ||
    typeof semanticWeight !== "number" ||
    typeof textWeight !== "number"
  ) {
    return null;
  }

  return {
    activityWeight,
    semanticWeight,
    textWeight,
    // Older stored weights predate the WE recency tuner; fall back to default.
    weRecencyWeight:
      typeof weRecencyWeight === "number"
        ? weRecencyWeight
        : DEFAULT_SEARCH_WEIGHTS.weRecencyWeight,
  };
};

const parseStoredFilters = (value: unknown): StoredSearchV2Filters | null => {
  if (!isRecord(value)) return null;

  const { expiresAt, filters, weights } = value;
  if (typeof expiresAt !== "number" || !isRecord(filters)) return null;

  const { includePastSubmitted, includeRejected, payCeiling, title } = filters;
  const keywords = parseStoredKeywords(filters.keywords);
  if (
    keywords === null ||
    typeof payCeiling !== "string" ||
    typeof includeRejected !== "boolean" ||
    !isStringArray(title)
  ) {
    return null;
  }

  return {
    expiresAt,
    filters: {
      // Added after the first release, so older entries won't have it. Legacy
      // entries hold the inverted `excludeAllPlacements` — ignored on purpose,
      // so they fall back to the new default (excluded).
      includePastSubmitted: includePastSubmitted === true,
      includeRejected,
      keywords,
      payCeiling,
      title,
    },
    weights: parseStoredWeights(weights),
  };
};

const getStoredFiltersByJobId = (): Record<
  string,
  StoredSearchV2Filters | undefined
> => {
  const rawFilters = localStorage.getItem(SEARCH_V2_FILTERS_STORAGE_KEY);
  if (!rawFilters) return {};

  let parsedFilters: unknown = null;
  try {
    parsedFilters = JSON.parse(rawFilters);
  } catch {
    return {};
  }
  if (!isRecord(parsedFilters)) return {};

  const filtersByJobId: Record<string, StoredSearchV2Filters> = {};
  for (const [jobId, storedFilters] of Object.entries(parsedFilters)) {
    const parsed = parseStoredFilters(storedFilters);
    if (parsed) filtersByJobId[jobId] = parsed;
  }

  return filtersByJobId;
};

export const searchV2FiltersStorage = {
  get: (jobId: string): StoredSearchV2Settings | null => {
    const filtersByJobId = getStoredFiltersByJobId();
    const storedFilters = filtersByJobId[jobId];
    if (!storedFilters) return null;

    if (storedFilters.expiresAt <= Date.now()) {
      const remainingFilters = Object.fromEntries(
        Object.entries(filtersByJobId).filter(([key]) => key !== jobId),
      );
      localStorage.setItem(
        SEARCH_V2_FILTERS_STORAGE_KEY,
        JSON.stringify(remainingFilters),
      );

      return null;
    }

    return {
      filters: storedFilters.filters,
      weights: storedFilters.weights,
    };
  },
  set: (jobId: string, filters: SearchV2Filters, weights: SearchV2Weights) => {
    const filtersByJobId = getStoredFiltersByJobId();
    filtersByJobId[jobId] = {
      expiresAt: Date.now() + SEARCH_V2_FILTERS_TTL_MS,
      filters,
      weights,
    };
    localStorage.setItem(
      SEARCH_V2_FILTERS_STORAGE_KEY,
      JSON.stringify(filtersByJobId),
    );
  },
};
